import React, { useState } from 'react';
import { Cta } from './Cta';
import { ChevronDown } from 'lucide-react';
import { Reveal } from './Reveal';
import { PathThread } from './PathThread';
import { useScore } from './ScoreContext';
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-106">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={() => setIsOpen(!isOpen)} data-id="element-107">
        <span className="text-lg font-serif text-navy group-hover:text-seaglass transition-colors" data-id="element-108">
          {question}
        </span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-paper-dark transition-colors ${isOpen ? 'bg-seaglass/10' : 'group-hover:bg-paper'}`} data-id="element-109">
          <ChevronDown className={`w-5 h-5 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180 text-seaglass' : ''}`} data-id="element-110" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'}`} data-id="element-111">
        <div className="text-text-muted leading-relaxed pr-8" data-id="element-112">{answer}</div>
      </div>
    </div>;
}
export function FinishFaqCta() {
  const faqs = [{
    q: 'Is this book marketing?',
    a: 'No. Book marketing gets more people to the book. Author Leverage builds the path for serious readers after the book creates trust.'
  }, {
    q: 'Is this a funnel?',
    a: 'Not in the usual sense. A funnel pushes people toward a predetermined sale. This helps readers take a useful next step based on where they are.'
  }, {
    q: 'What if my book has not launched yet?',
    a: 'This can be built before, during, or after launch. The page is written for authors whose book is already in the world because that is where the gap is easiest to feel. But pre-launch authors can still build the path before readers arrive.'
  }, {
    q: 'Will I know exactly who bought my book?',
    a: 'No. Amazon and most retailers do not give you reader data. This gives serious readers a useful reason to raise their hand, so you can see who wants to keep going.'
  }, {
    q: 'Do I need a huge audience?',
    a: 'No. If your book supports high-value work, a small number of the right readers taking a useful next step can matter.'
  }, {
    q: 'Do you guarantee revenue?',
    a: 'No. The build gives serious readers a clearer path and gives you a better way to see what they may need. Revenue depends on your offer, audience, follow-up, and fit.'
  }, {
    q: 'How much time does this take from me?',
    a: 'We do the heavy lifting. We need your judgment, your point of view, and a few focused working sessions to extract the core ideas, review the path, and approve the build.'
  }, {
    q: 'What happens after the diagnostic?',
    a: 'You get your Reader-to-Revenue Score. If there is a gap we can help fix, the next step is a Reader Path Audit.'
  }, {
    q: 'Do I have to manage the tech?',
    a: 'No. The build is designed to work behind your existing book, site, and offers without turning you into the tech manager.'
  }];
  const {
    answeredCount,
    openDiagnostic
  } = useScore();
  const ctaLabel = answeredCount === 0 ? 'Get Your Reader-to-Revenue Score' : answeredCount < 4 ? 'Continue Your Score' : 'Finish Your Score';
  const handleCtaClick = () => {
    openDiagnostic();
  };
  const stateAwareCopy = answeredCount === 0 ? 'Start with 8 questions about what happens after someone reads your book.' : answeredCount < 4 ? 'You have already started. Finish the remaining questions to get your Reader-to-Revenue Score.' : 'You are halfway there. Answer the remaining questions to get your Reader-to-Revenue Score.';
  return <section id="final-score-block" className="py-24 px-6 max-w-3xl mx-auto relative scroll-mt-24" data-id="element-113">
      <div className="absolute -top-12 left-1/2 -translate-x-1/2" data-id="element-114">
        <PathThread data-id="element-115" />
      </div>

      {/* Process Visual */}
      <div className="text-center mb-32" data-id="element-116">
        <Reveal data-id="element-117">
          <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 leading-tight" data-id="element-118">
            Get the score before you <br data-id="element-119" />
            <span className="italic text-seaglass" data-id="element-120">decide what to build</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} data-id="element-121">
          <p className="text-lg text-text-muted mb-16" data-id="element-122">{stateAwareCopy}</p>
        </Reveal>

        <Reveal delay={0.2} data-id="element-123">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8" data-id="element-124">
            <div className="flex flex-col items-center gap-4" data-id="element-125">
              <div className="w-14 h-14 rounded-full bg-seaglass text-white flex items-center justify-center font-serif text-xl shadow-[0_4px_12px_-4px_rgba(139,168,159,0.6)]" data-id="element-126">
                1
              </div>
              <div className="text-sm font-medium text-navy text-center max-w-[120px]" data-id="element-127">
                Get your Reader-to-Revenue Score
              </div>
            </div>
            <div className="hidden md:block w-16 h-px bg-seaglass border-t border-dashed border-seaglass" data-id="element-128"></div>
            <div className="flex flex-col items-center gap-4 opacity-60" data-id="element-129">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 text-gray-400 flex items-center justify-center font-serif text-xl" data-id="element-130">
                2
              </div>
              <div className="text-sm font-medium text-gray-500 text-center max-w-[120px]" data-id="element-131">
                Audit the path
              </div>
            </div>
            <div className="hidden md:block w-16 h-px bg-gray-300 border-t border-dashed border-gray-300" data-id="element-132"></div>
            <div className="flex flex-col items-center gap-4 opacity-60" data-id="element-133">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 text-gray-400 flex items-center justify-center font-serif text-xl" data-id="element-134">
                3
              </div>
              <div className="text-sm font-medium text-gray-500 text-center max-w-[120px]" data-id="element-135">
                Decide whether to build
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* FAQ */}
      <div className="mb-32" data-id="element-136">
        <Reveal data-id="element-137">
          <h3 className="text-3xl font-serif text-navy mb-10 text-center" data-id="element-138">
            Frequently Asked Questions
          </h3>
        </Reveal>
        <Reveal delay={0.1} data-id="element-139">
          <div className="border-t border-gray-200" data-id="element-140">
            {faqs.map((faq, idx) => <FaqItem key={idx} question={faq.q} answer={faq.a} data-id="element-141" />)}
          </div>
        </Reveal>
      </div>

      {/* Final CTA */}
      <Reveal data-id="element-142">
        <div className="text-center bg-white rounded-[2.5rem] p-12 md:p-16 border border-gray-100 shadow-[0_20px_40px_-15px_rgba(26,40,56,0.1)] relative overflow-hidden" data-id="element-143">
          <div className="absolute top-0 left-0 right-0 h-2 bg-seaglass" data-id="element-144"></div>
          <h2 className="text-3xl md:text-5xl font-serif text-navy mb-10 leading-tight" data-id="element-145">
            You already did the hard part. <br data-id="element-146" />
            <span className="italic text-seaglass" data-id="element-147">
              Now see what happens after someone reads it.
            </span>
          </h2>
          <div className="flex flex-col items-center gap-5" data-id="element-148">
            <Cta variant="primary" showArrow onClick={handleCtaClick} className="shadow-[0_8px_16px_-8px_rgba(212,165,116,0.5)]" data-id="element-149">
              {ctaLabel}
            </Cta>
            <span className="text-sm text-text-muted font-medium" data-id="element-150">
              8 questions. No manuscript upload required.
            </span>
          </div>
        </div>
      </Reveal>
    </section>;
}